import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AccountService } from '@jfw/angular/shared/services';
import { AnnouncementListItem } from '@jfw/types';

const PAGE_SIZE = 10;

@Component({
  selector: 'app-announcements',
  templateUrl: './announcements.component.html',
  styleUrls: ['./announcements.component.scss'],
  imports: [
    CommonModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatPaginatorModule,
    MatTooltipModule,
  ],
})
export class AnnouncementsComponent {
  private readonly accountService = inject(AccountService);

  protected readonly pageSize = PAGE_SIZE;
  private pageIndex = 0; // zero-based, to match mat-paginator's own convention
  protected includeArchives = false;

  protected readonly announcementsPage = this.accountService._announcementsPage;
  protected readonly loading = this.accountService.announcementsLoader;
  protected readonly error = this.accountService.announcementsError;

  protected readonly totalCount = computed(
    () => this.announcementsPage()?.totalCount ?? 0,
  );

  // Deferred until platformAccount() resolves, same as ShippingInfoComponent
  // -- announcementsApiUrl is only set by app.component.ts's own effect once
  // the account resolves, so calling load() unconditionally here would race
  // that and hit the "API URL not set yet" empty-observable guard.
  constructor() {
    effect(() => {
      if (this.accountService._platformAccount()) {
        this.load();
      }
    });
  }

  private load(): void {
    this.accountService.loadAnnouncements(
      this.pageIndex + 1,
      this.pageSize,
      this.includeArchives,
    );
  }

  protected toggleIncludeArchives(includeArchives: boolean): void {
    this.includeArchives = includeArchives;
    this.pageIndex = 0;
    this.load();
  }

  protected onPage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.load();
  }

  // window.open is called synchronously, before the async mark-read call --
  // calling it after an await gets silently blocked by popup blockers (see
  // the WholesaleRviDocumentService business rule for the same pattern).
  protected async openLink(item: AnnouncementListItem): Promise<void> {
    if (item.link) {
      window.open(item.link, '_blank', 'noopener');
    }
    await this.accountService.markAnnouncementRead(item.id);
  }

  protected async archive(item: AnnouncementListItem): Promise<void> {
    await this.accountService.archiveAnnouncement(item.id);
  }

  protected async restore(item: AnnouncementListItem): Promise<void> {
    await this.accountService.restoreAnnouncement(item.id);
  }

  protected formatPublishedDate(publishedDate: number | null): string {
    if (!publishedDate) return '';
    return new Date(publishedDate * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
}
